import React, { useMemo } from 'react';
import {
    type Layout,
    LAYOUT_FORMS_FULL,
    useAdditionnalCollectionRequest,
    useCurrentDossierCtx,
    useDossierFormulaireContext,
    useDossierProcesses,
    useDossierRoutingContext,
    useTransition,
} from '@europrocurement/l2d-modules';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { useNavigate } from 'react-router';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { ApiDossiersIdActionPutActionEnum } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import DossierFormsBody from '../DossierFormsBody/DossierFormsBody';
import DossierValidationActions from '../DossierValidationActions/DossierValidationActions';
import { useDossierFormValidation } from '../../hooks';

export type DossierFormsValidationProps = {
    currentLayout: Layout;
};

const DossierFormsValidation: React.FunctionComponent<DossierFormsValidationProps> = function ({
    currentLayout,
}) {
    const {
        dataLoaded,
        getCurrentFormulaire,
        persistAllFormulaires,
        updateFormulaire,
        hasNonPersistedChanges,
    } = useDossierFormulaireContext();
    const { dossier } = useCurrentDossierCtx();
    const { cancel } = useDossierFormValidation();
    const { canApplyTransition, applyTransition } = useTransition();
    const { getDossierRoute } = useDossierRoutingContext();
    const navigate = useNavigate();
    const { modalActions } = useModal();
    const { enqueueSnackbar } = useSnackbar();
    const { publishGreffeValidatorProcess, canPublish, getNotPublishableReason } =
        useDossierProcesses();
    const { callAdditionnalCollectionRequestModal } = useAdditionnalCollectionRequest();
    const { palette } = useTheme();

    const canDepose = useMemo(() => (dossier ? canPublish(dossier) : false), [canPublish, dossier]);
    const notDeposableReason = useMemo(
        () => (dossier ? getNotPublishableReason(dossier) : undefined),
        [getNotPublishableReason, dossier],
    );

    if (!dataLoaded) {
        return (
            <Box
                data-testid="form-validation-loading-icon"
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100%',
                    padding: '1em 0',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const formulaire = getCurrentFormulaire();

    const handleChange = (data: Record<string, unknown>) => {
        if (formulaire) {
            updateFormulaire({
                ...formulaire,
                prestationJson: _.merge({}, formulaire.prestationJson, data),
            });
        }
    };

    const handleCancel = () => {
        if (formulaire) {
            cancel(formulaire.dossierId);
        }
    };

    const handleSendMessage = () => {
        if (hasNonPersistedChanges) {
            modalActions.call(
                <ConfirmationModal
                    messages={{
                        question: 'Enregistrer les modifications ?',
                        detail: {
                            message:
                                'Vous avez des modifications en cours sur le formulaire. Souhaitez-vous sauvegarder vos modifications?',
                            severity: 'warning' as const,
                        },
                        validation: 'Sauvegarder',
                        annulation: 'Continuer',
                    }}
                    color={palette.primary}
                    actionOnValidation={async () => {
                        await persistAllFormulaires();
                        callAdditionnalCollectionRequestModal();
                    }}
                    actionOnCancellation={() => {
                        callAdditionnalCollectionRequestModal();
                    }}
                />,
            );
        } else {
            callAdditionnalCollectionRequestModal();
        }
    };

    const handleSubmitAndPublish = async () => {
        if (!dossier) {
            return;
        }
        // Open the tab immediately on user gesture to avoid browser popup blocking
        const newTab = window.open('', '_blank');
        try {
            await persistAllFormulaires();
            const response = await publishGreffeValidatorProcess(dossier);

            if (response.data?.externalUrl && newTab) {
                newTab.location.href = response.data.externalUrl as string;
            } else {
                newTab?.close();
            }

            navigate(getDossierRoute(dossier.id as unknown as number));
            enqueueSnackbar('Votre dossier a bien été sauvegardé et déposé au greffe');
        } catch (err) {
            newTab?.close();
            console.error(err);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du dossier', {
                variant: 'error',
            });
        }
    };

    const handleSubmit = async (actionIdentifier: ApiDossiersIdActionPutActionEnum | 'save') => {
        if (!dossier) {
            return;
        }
        try {
            await persistAllFormulaires();
            if (actionIdentifier !== 'save') {
                if (canApplyTransition(dossier, actionIdentifier)) {
                    await applyTransition(dossier, actionIdentifier);
                } else {
                    enqueueSnackbar('Statut non disponible pour ce dossier', {
                        variant: 'error',
                    });
                }
            }

            enqueueSnackbar('Votre dossier a bien été sauvegardé');
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue lors de la sauvegarde du dossier', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const fullLayoutSx =
        currentLayout === LAYOUT_FORMS_FULL ? { maxWidth: '40rem', margin: 'auto' } : {};

    return (
        <Box
            sx={{
                height: '80vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: palette.background.paper,
                borderRadius: '12px',
                ...fullLayoutSx,
            }}
        >
            {formulaire && (
                <DossierFormsBody
                    formulaire={formulaire}
                    onChange={handleChange}
                />
            )}

            <DossierValidationActions
                onCancel={handleCancel}
                onSendMessage={handleSendMessage}
                onValidate={() => handleSubmit('save')}
                onPublish={handleSubmitAndPublish}
                canPublish={canDepose}
                getNotPublishableReason={notDeposableReason}
            />
        </Box>
    );
};

export default DossierFormsValidation;
